import { useState, useEffect } from "react"
import "./users.css"
import Loading from "../Templates/Loading"
import { getUserList, updateUserStatus, getUserProjects, setUserProjects, getProject } from "../../Service"
import { UserType } from "../DropdownOptions"
import SearchContainer from "../Search/Search"
import AddUser from "../AddUser/AddUser"
import Pagination from "../Templates/Pagination"
import HeaderNav from "../Templates/HeaderNav"
import ConfirmModal from "../Templates/ConfirmModal"
import { toast } from "react-toastify"

const Users = () => {
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortOrder, setSortOrder] = useState("nor")
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filters, setFilters] = useState({ status: "", role: "" });
  const [duplicateArray, setDuplicateArray] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const maxPageButtons = 5;

  // Project access editor
  const [accessUser, setAccessUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [accessSelection, setAccessSelection] = useState([]);
  const [accessAllProjects, setAccessAllProjects] = useState(false);

  // User whose enable/disable is awaiting confirmation.
  const [pendingUser, setPendingUser] = useState(null);

  const currentUserId = localStorage.getItem("userId");


  useEffect(() => {
    fetchUsers()
  }, [])

  function formatDate(isoString) {
    const date = new Date(isoString);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  }

  const getRandomColor = () => {
    const colors = ["#FF5733", "#3357FF", "#FF33A1", "#FF8C33", "#8C33FF"];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  useEffect(() => {
    handleSearchAndFilter()
  }, [users, searchTerm, sortOrder])

  const fetchUsers = async () => {
    try {
      const response = await getUserList("/users")
      const usersArray = Array.isArray(response.data) ? response.data : [response.data]
      setUsers(usersArray)
      setIsLoading(false)
    } catch (error) {
      setError(error.message)
      setIsLoading(false)
    }
  }

  const handleSearchAndFilter = () => {
    let filtered = [...users]
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (UserType.find(u => u.value === user.role)?.type || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    filtered.sort((a, b) =>
      sortOrder === "nor" ? null : sortOrder === "asc" ? a.full_name.localeCompare(b.full_name) : b.full_name.localeCompare(a.full_name)
    )
    setFilteredUsers(filtered)
  }

  const handleAddUser = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    fetchUsers()
  }

  // Enable/disable. Login already refuses any account whose Status is not
  // "Active", so this is all that gating a user takes.
  const handleToggleStatus = (user) => {
    const nextStatus = user.Status === "Active" ? "Disabled" : "Active"

    if (String(user.user_id) === String(currentUserId) && nextStatus === "Disabled") {
      toast.warning("You cannot disable your own account.")
      return
    }

    // Ask for confirmation via the app-styled modal instead of window.confirm.
    setPendingUser(user)
  }

  const confirmToggleStatus = async () => {
    const user = pendingUser
    setPendingUser(null)
    if (!user) return

    const nextStatus = user.Status === "Active" ? "Disabled" : "Active"
    setIsLoading(true)
    try {
      await updateUserStatus(`/users/${user.user_id}/status`, { status: nextStatus })
      await fetchUsers()
    } catch (error) {
      console.error("Error updating user status:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const openAccessModal = async (user) => {
    setAccessUser(user)
    setIsLoading(true)

    try {
      const [projectResponse, accessResponse] = await Promise.all([
        getProject("/projects"),
        getUserProjects(`/users/${user.user_id}/projects`),
      ])

      setProjects(Array.isArray(projectResponse) ? projectResponse : [])
      setAccessAllProjects(Boolean(accessResponse?.data?.all_projects_access))
      setAccessSelection((accessResponse?.data?.projects || []).map((project) => project.project_id))
    } catch (error) {
      console.error("Error loading project access:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const closeAccessModal = () => {
    setAccessUser(null)
    setAccessSelection([])
    setAccessAllProjects(false)
  }

  const toggleAccessProject = (projectId) => {
    setAccessSelection((previous) =>
      previous.includes(projectId)
        ? previous.filter((id) => id !== projectId)
        : [...previous, projectId]
    )
  }

  const saveAccess = async () => {
    if (!accessAllProjects && accessSelection.length === 0) {
      toast.warning("Assign at least one project, or tick 'All projects'.")
      return
    }

    setIsLoading(true)
    try {
      await setUserProjects(`/users/${accessUser.user_id}/projects`, {
        all_projects_access: accessAllProjects,
        project_ids: accessAllProjects ? [] : accessSelection,
      })
      closeAccessModal()
      await fetchUsers()
    } catch (error) {
      console.error("Error saving project access:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFilter = () => {
    setFilterModalOpen(true);
  }

  const handleReset = () => {
    setFilters({ status: "", role: "" });
  }
  const closeFilter = () => {
    setFilterModalOpen(false);
  }
  const applyFilters = async () => {
    setFilterModalOpen(false);
    setIsLoading(true);
    try {
        let queryParams = [];
        if (filters.status) queryParams.push(`status=${filters.status}`);
        if (filters.role) queryParams.push(`role=${filters.role}`);

        const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
        const response = await getUserList(`/users${queryString}`);

        const usersArray = Array.isArray(response.data) ? response.data : [response.data];
        setUsers(usersArray);
        setFilteredUsers(usersArray);
    } catch (err) {
        setError("Failed to apply filters. Please try again later.");
    }
    setIsLoading(false);
  };

   useEffect(() => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;

      setDuplicateArray(filteredUsers.slice(startIndex, endIndex));
    }, [filteredUsers, currentPage]);


    const getVisiblePages = () => {
      const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

      let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
      let endPage = startPage + maxPageButtons - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxPageButtons + 1);
      }

      return Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
      );
    };

    const visiblePages = getVisiblePages();


    const shouldShowEllipses = () => {
      const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
      const remainingPages = totalPages - currentPage;

      return remainingPages >= maxPageButtons - 2;
    };

  return (
    <div className="users-container">
      <HeaderNav
        name="Users"
        button_name="Add User"
        buttonClick={handleAddUser}
      />

      <SearchContainer
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setSortOrder={setSortOrder}
        handleFilter={handleFilter}
      />

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Project Access</th>
              <th>Failed Login Attempts</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
          {duplicateArray.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>No data found</td>
              </tr>
            ) : (
            duplicateArray.map((user) => (
              <tr key={user.user_id}>
                <td>
                  <div className="name-cell">
                      <div
                          className="avatar"
                          style={{ backgroundColor: getRandomColor(), color: "#fff", fontWeight: "bold", fontSize:"12px"}}
                      >
                          {user.full_name && typeof user.full_name === "string"
                          ? user.full_name
                              .split(" ")
                              .map(word => word.charAt(0).toUpperCase())
                              .join("")
                          : ""}
                      </div>
                      <div style={{display:"column"}}>
                          <span>{user.full_name}</span>
                          <p style={{fontSize:"10px", color:"gray"}}>
                              {UserType.find(u => u.value == user.role)?.type}
                          </p>
                      </div>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span
                    style={{
                      fontSize: "12px",
                      display: "inline-block",
                      padding: "4px",
                      minWidth: "80px",
                      textAlign: "center",
                      fontWeight: "bold",
                      color: user.Status === "Active" ? "#1E7E34" :
                        user.Status === "Disabled" ? "#DC3545" : "black" ,
                      backgroundColor: user.Status === "Active" ? "#E6F4EA" :
                        user.Status === "Disabled" ? "#FDE7E9" : "#ddd" ,
                      borderRadius:"10px",
                    }}
                  >
                    {user.Status}
                  </span>
                </td>
                <td>
                  <span style={{ fontSize: "12px" }}>
                    {Number(user.all_projects_access) === 1 || [1, 2].includes(Number(user.role))
                      ? "All projects"
                      : "Assigned only"}
                  </span>
                </td>
                <td>{user.FailedLoginAttempts}</td>
                <td>{formatDate(user.created_at)}</td>
                <td>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button
                      type="button"
                      className="btn btn-outline"
                      style={{ fontSize: "11px", padding: "4px 8px" }}
                      onClick={() => openAccessModal(user)}
                    >
                      Projects
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      style={{
                        fontSize: "11px",
                        padding: "4px 8px",
                        color: user.Status === "Active" ? "#DC3545" : "#1E7E34",
                        borderColor: user.Status === "Active" ? "#DC3545" : "#1E7E34",
                      }}
                      onClick={() => handleToggleStatus(user)}
                    >
                      {user.Status === "Active" ? "Disable" : "Enable"}
                    </button>
                  </div>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
        <Pagination
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          shouldShowEllipses={shouldShowEllipses}
          duplicateArray={filteredUsers}
          itemsPerPage={itemsPerPage}
          visiblePages={visiblePages}
        />
      </div>

      {isLoading && <Loading show={isLoading} /> }

      <ConfirmModal
        open={!!pendingUser}
        title={pendingUser ? `${pendingUser.Status === "Active" ? "Disable" : "Enable"} user` : ""}
        message={
          pendingUser
            ? `Are you sure you want to ${pendingUser.Status === "Active" ? "disable" : "enable"} ${pendingUser.full_name}?`
            : ""
        }
        confirmLabel={pendingUser?.Status === "Active" ? "Disable" : "Enable"}
        confirmVariant={pendingUser?.Status === "Active" ? "danger" : "primary"}
        onConfirm={confirmToggleStatus}
        onCancel={() => setPendingUser(null)}
      />

      {isModalOpen && <AddUser isOpen={isModalOpen} onClose={handleCloseModal} />}

      {accessUser && (
        <div className='modal-overlay'>
          <div className="filter-modal">
            <div className='filter-header'>
              <h3 style={{margin: "0"}}>Project Access - {accessUser.full_name}</h3>
              <h3 onClick={closeAccessModal} style={{margin: "0", cursor: "pointer"}}>X</h3>
            </div>
            <div className="modal-content">
              {[1, 2].includes(Number(accessUser.role)) && (
                <p style={{ fontSize: "12px", color: "gray" }}>
                  Admins can see every project regardless of what is set here.
                </p>
              )}

              <label style={{ display: "flex", alignItems: "center", gap: "8px", margin: "6px 0", fontWeight: "bold" }}>
                <input
                  type="checkbox"
                  checked={accessAllProjects}
                  onChange={(e) => setAccessAllProjects(e.target.checked)}
                  style={{ width: "16px", height: "16px" }}
                />
                All projects
              </label>
              <p style={{ fontSize: "11px", color: "gray", margin: "0 0 8px" }}>
                Includes projects created in the future.
              </p>

              {!accessAllProjects && (
                <div
                  style={{
                    maxHeight: "180px",
                    overflowY: "auto",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  {projects.length === 0 ? (
                    <p style={{ fontSize: "12px", color: "gray", margin: 0 }}>No projects available.</p>
                  ) : (
                    projects.map((project) => (
                      <label
                        key={project.project_id}
                        style={{ display: "flex", alignItems: "center", gap: "8px", margin: "4px 0" }}
                      >
                        <input
                          type="checkbox"
                          checked={accessSelection.includes(project.project_id)}
                          onChange={() => toggleAccessProject(project.project_id)}
                          style={{ width: "16px", height: "16px" }}
                        />
                        {project.project_name}
                        <span style={{ fontSize: "11px", color: "gray" }}>({project.project_key})</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="filter-footer">
              <div />
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button type="button" className="btn btn-outline" onClick={closeAccessModal}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={saveAccess}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {filterModalOpen && (
        <div className='modal-overlay'>
          <div className="filter-modal">
            <div className='filter-header'>
              <h3 style={{margin: "0"}}>Filter Users</h3>
              <h3 onClick={closeFilter} style={{margin: "0"}}>X</h3>
            </div>
            <div className="modal-content">
              <div className='form-group'>
                <label>Status</label>
                <select
                  required
                  className="form-control"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">Select status</option>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>

              <div className='form-group'>
                <label>Role</label>
                <select
                  required
                  className="form-control"
                  value={filters.role}
                  onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                >
                  <option value="">Select role</option>
                  {UserType.map(user => (
                    <option key={user.value} value={user.value}>
                      {user.type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="filter-footer">
              <div>
                <button type="button" className="btn btn-secondary" onClick={handleReset}>Reset</button>
              </div>
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button type="button" className="btn btn-outline" onClick={closeFilter}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={applyFilters}>
                    Apply
                  </button>
                </div>
              </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Users
